import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table as TableAntd, Flex } from 'antd';
import { Input } from 'formik-antd';
import FormControl from '~/components/Form/FormControl';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { getOperationals } from '~/services/hooks/operationals';
import Strong from '~/components/Strong';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { FieldArray } from 'formik';
import { Table } from '~/components/Table';
import { sectorOperationalColumns } from '~/tables/sectorOperationals';
import Button from '~/components/Button';
import { formatPrice, parsePrice } from '~/Utils/format';
import errorHandler from '~/Utils/errorHandler';

export const OperationalModal = ({ t, values, setLoading, setFieldValue, visible, errors, eventRevenues }) => {
  const authCheck = useCheckAuth();
  const [operationals, setOperationals] = useState([]);

  const fetchOperationals = async () => {
    setLoading(true);
    try {
      const operationals = await getOperationals('', 'no-paginated');
      setOperationals(operationals);
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const { totalOperators, totalPaid } = useMemo(() => {
    let operatorsCount = 0;
    let paidSum = 0;

    values?.sectors?.forEach((sector) => {
      sector?.sector_operationals?.forEach((op) => {
        operatorsCount += Number(op.amount || 0);

        paidSum += parsePrice(op.price_total || 0);
      });
    });

    return { totalOperators: operatorsCount, totalPaid: paidSum };
  }, [values?.sectors]);

  const fluidPermissions = () => {
    if (eventRevenues) {
      return !eventRevenues;
    }
    return authCheck('@events/operationalEdit');
  };

  useEffect(() => {
    if (visible) {
      fetchOperationals();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && values) {
      values?.sectors?.map((sector, sectorIndex) => {
        sector?.sector_operationals?.map((sectorOp, sectorOperationalIndex) => {
          const amount = parsePrice(sectorOp?.amount ?? 0);
          const priceUnit = parsePrice(sectorOp?.price_unit ?? 0);
          const newPriceTotal = priceUnit * amount;

          setFieldValue(
            `sectors.${sectorIndex}.sector_operationals.${sectorOperationalIndex}.price_total`,
            newPriceTotal
          );
        });
      });

      values?.sectors?.map((sector, index) => {
        const sectorTotal = sector?.sector_operationals?.reduce((sum, op) => {
          return sum + parsePrice(op.price_total);
        }, 0);

        setFieldValue(`sectors.${index}.sector_total_price`, sectorTotal);
      });
    }
  }, [visible, JSON.stringify(values.sectors)]);

  return (
    <>
      {values?.sectors?.map((_, sectorIndex) => (
        <Card
          key={sectorIndex}
          title={
            <FormControl
              label={t('fields:event.sector.name')}
              field={`sectors.${sectorIndex}.name`}
              error={errors?.sectors?.[sectorIndex]?.name}
              required
              cols={{ sm: 12 }}
            >
              <Input name={`sectors.${sectorIndex}.name`} disabled={!fluidPermissions()} style={{ marginTop: 10 }} />
            </FormControl>
          }
          extra={
            <Button
              icon={<FaTrash />}
              color="danger"
              onClick={() => {
                values?.sectors?.splice(sectorIndex, 1);
                setFieldValue(values);
              }}
            >
              Remover Setor
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <FieldArray name={`sectors.${sectorIndex}.sector_operationals`}>
            {({ push, remove }) => (
              <Table
                dataSource={values?.sectors[sectorIndex]?.sector_operationals?.map((sectorOperational, index) => ({
                  ...sectorOperational,
                  key: index,
                  sectorIndex,
                  remove,
                }))}
                columns={sectorOperationalColumns(operationals, errors, fluidPermissions, values, setFieldValue)}
                pagination={false}
                size="small"
                rowClassName={() => 'editable-row'}
                bordered
                empty={false}
                summary={() => {
                  const totals = values?.sectors[sectorIndex]?.sector_operationals?.reduce(
                    (acc, op) => {
                      acc.priceTotal += parsePrice(op.price_total);
                      acc.amountTotal += parsePrice(op.amount);

                      return acc;
                    },
                    {
                      priceTotal: 0,
                      amountTotal: 0,
                    }
                  );

                  return (
                    <>
                      <TableAntd.Summary.Row>
                        <TableAntd.Summary.Cell>
                          <Flex style={{ height: '100%' }} align="flex-start" justify="center">
                            <Button
                              color="primary"
                              title="Adicionar operacional"
                              size="small"
                              onClick={() =>
                                push({
                                  amount: null,
                                  price_unit: null,
                                  invite_price: null,
                                  price_total: null,
                                  operational_id: null,
                                  operational_name: null,
                                })
                              }
                              style={{ width: '30px' }}
                              disabled={!fluidPermissions()}
                            >
                              <FaPlus />
                            </Button>
                          </Flex>
                        </TableAntd.Summary.Cell>
                      </TableAntd.Summary.Row>
                      <TableAntd.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                        <TableAntd.Summary.Cell>
                          <Strong>{t('TOTAL')}</Strong>
                        </TableAntd.Summary.Cell>
                        <TableAntd.Summary.Cell>{totals.amountTotal}</TableAntd.Summary.Cell>
                        <TableAntd.Summary.Cell />
                        <TableAntd.Summary.Cell>{formatPrice(totals.priceTotal)}</TableAntd.Summary.Cell>
                        <TableAntd.Summary.Cell />
                      </TableAntd.Summary.Row>
                    </>
                  );
                }}
              />
            )}
          </FieldArray>
        </Card>
      ))}
      <Flex align="center" justify="center">
        <Button
          icon={<FaPlus />}
          color="primary"
          onClick={() => {
            values.sectors.push({ name: '', sector_operationals: [] });
            setFieldValue(values);
          }}
          disabled={!fluidPermissions()}
        >
          Adicionar Setor
        </Button>
      </Flex>
      <Flex justify="end" style={{ marginBottom: 20 }}>
        <Card style={{ borderColor: '#d6d6d6' }}>
          <Flex vertical align="start" justify="start" gap={10}>
            <Strong>{t('TOTAL OPERADORES: ') + totalOperators}</Strong>
            <Strong>{t('TOTAL PAGAS: ') + formatPrice(totalPaid)}</Strong>
          </Flex>
        </Card>
      </Flex>
    </>
  );
};
